<script lang="ts">
	import { onDestroy } from 'svelte';
	import { fade } from 'svelte/transition';
	import { browser } from '$app/environment';

	export let showModal = false;
	export let title: string | undefined = undefined;
	export let noScroll = false;

	let modalContent: HTMLElement;
	let previousActiveElement: Element | null;

	function handleKeydown(event: KeyboardEvent) {
		if (event.key === 'Escape') {
			showModal = false;
		}
	}

	function disableBodyScroll() {
		if (!browser) return;
		document.body.style.overflow = 'hidden';
		document.body.style.paddingRight = `${window.innerWidth - document.documentElement.clientWidth}px`;
	}

	function enableBodyScroll() {
		if (!browser) return;
		document.body.style.overflow = '';
		document.body.style.paddingRight = '';
	}

	$: if (showModal && browser) {
		disableBodyScroll();
		// Store the currently focused element
		// eslint-disable-next-line no-useless-assignment -- restored when the modal closes
		previousActiveElement = document.activeElement;
		// Set focus to modal content after render
		setTimeout(() => {
			modalContent?.focus();
		}, 0);
	} else if (browser) {
		enableBodyScroll();
		// Restore focus to the previous element
		if (previousActiveElement instanceof HTMLElement) {
			previousActiveElement.focus();
		}
	}

	onDestroy(() => {
		if (browser) {
			enableBodyScroll();
		}
	});
</script>

{#if showModal}
	<div
		class="fixed inset-0 z-50 flex items-center justify-center overflow-x-hidden overflow-y-auto p-2"
		role="dialog"
		aria-modal="true"
		{...title ? { 'aria-labelledby': 'modal-title' } : {}}
		bind:this={modalContent}
		tabindex="-1"
	>
		<!-- Overlay -->
		<div
			class="fixed inset-0 bg-black/50 transition-opacity"
			transition:fade={{ duration: 200 }}
			on:click={() => (showModal = false)}
			on:keydown={handleKeydown}
			aria-label="Close modal overlay"
			role="button"
			tabindex="0"
		></div>

		<!-- Modal -->
		<div
			class="relative z-50 mx-auto h-[calc(100vh-1rem)] max-h-full w-full rounded-lg bg-white shadow-xl sm:h-[calc(100vh-2rem)] sm:max-w-lg md:max-w-2xl lg:max-w-4xl dark:bg-gray-800 {noScroll
				? 'overflow-hidden'
				: 'overflow-y-auto'}"
		>
			<!-- Close button -->
			<button
				type="button"
				class="absolute top-3 right-2.5 ml-auto inline-flex h-8 w-8 items-center justify-center rounded-lg bg-transparent text-sm text-gray-400 hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-gray-600 dark:hover:text-white"
				on:click={() => (showModal = false)}
				aria-label="Close modal"
			>
				<svg
					class="h-3 w-3"
					aria-hidden="true"
					xmlns="http://www.w3.org/2000/svg"
					fill="none"
					viewBox="0 0 14 14"
				>
					<path
						stroke="currentColor"
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
					/>
				</svg>
			</button>

			{#if title}
				<h2
					id="modal-title"
					class="mb-4 px-6 pt-6 text-xl font-semibold sm:text-2xl dark:text-white"
				>
					{title}
				</h2>
			{/if}

			<div class="h-[calc(100%-theme(spacing.16))] px-6 pb-6 {title ? '' : 'pt-6'}">
				<slot />
			</div>
		</div>
	</div>
{/if}

<style>
</style>
